/**
 * Social text normalization — the canonical search/uniqueness key builder.
 *
 * `normalizeName` MUST stay byte-for-byte identical to the copy in
 * `flashka-web/scripts/seed-universities.ts`, because the universities dataset
 * already in Postgres was seeded with `name_norm` / `alias_norm` computed by that
 * script. The university-picker search normalizes the query with THIS function
 * and compares against those stored columns, and the consent flow stores
 * `social_profiles.name_norm` / `handle_norm` the same way — so any divergence
 * silently breaks prefix matching. The seed script keeps its own inline copy
 * (it must stay self-contained / dependency-free for the `tsx` pipeline); treat
 * this module as the source of truth and mirror edits into both.
 */

/**
 * Letters that don't split into base + combining mark under NFD, so the
 * diacritic strip alone would leave them unchanged. Mapped explicitly to cover
 * the common European cases the real dataset contains.
 */
const NON_DECOMPOSABLE: Record<string, string> = {
  ł: 'l',
  Ł: 'l',
  ø: 'o',
  Ø: 'o',
  đ: 'd',
  Đ: 'd',
  ð: 'd',
  Ð: 'd',
  þ: 'th',
  Þ: 'th',
  ß: 'ss',
  æ: 'ae',
  Æ: 'ae',
  œ: 'oe',
  Œ: 'oe',
  ı: 'i',
};

/** lowercase + strip diacritics + collapse whitespace — the search key. */
export function normalizeName(input: string): string {
  const mapped = Array.from(input)
    .map((ch) => NON_DECOMPOSABLE[ch] ?? ch)
    .join('');
  return mapped
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // combining diacritical marks
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Derive a username-safe handle slug from a display name: normalize
 * (lowercase + strip diacritics), then drop every non-alphanumeric character
 * (spaces, punctuation). Returns `''` when the name has no usable characters —
 * callers fall back to a userId-derived handle in that case.
 */
export function handleSlugFromName(name: string): string {
  return normalizeName(name).replace(/[^a-z0-9]/g, '');
}
